<template>
	<div class="flex">
		<DropdownStatus
			v-model="temporaryStatus"
 			:options="reviewStatusOptions"
			id="reviewStatus"
			checkmark
			optionLabel="title"
			optionValue="identifier"
			:disabled="disabled"
			@change="switchStatus"
			placeholder="Select Status"
			:class="[
				'w-full border',
				selectedreviewStatus?.identifier === 'f'
					? 'border-green-500 focus:border-green-500 active:border-green-500 outline-green-500'
				: selectedreviewStatus?.identifier === 'd'
					? 'border-warning-500 focus:border-warning-500 active:border-warning-500 outline-warning-500'
				: selectedreviewStatus?.identifier === 'e'
					? 'border-info-500 focus:border-info-500 active:border-info-500 outline-info-500'
				: selectedreviewStatus?.identifier === 't'
					? 'border-dark-500 focus:border-dark-500 active:border-dark-500 outline-dark-500'
				: 'border-danger-500 focus:border-danger-500 active:border-danger-500 outline-danger-500'
			]"
		>
			<template #value>
				<div
					class="flex items-center text-sm"
					:class="{
						'text-green-700': selectedreviewStatus?.identifier === 'f',
						'text-danger-700': selectedreviewStatus?.identifier === 'n',
						'text-warning-700': selectedreviewStatus?.identifier === 'd',
						'text-info-700': selectedreviewStatus?.identifier === 'e',
						'text-dark-700': selectedreviewStatus?.identifier === 't'
					}"
				>
					<i
						class="w-5 h-5 mr-1"
						:class="{
							'tabler:pencil-check color-green': selectedreviewStatus?.identifier === 'f',
							'tabler:pencil-off color-warning': selectedreviewStatus?.identifier === 'd',
							'tabler:pencil color-info': selectedreviewStatus?.identifier === 'e',
							'tabler:info-circle color-danger-700': selectedreviewStatus?.identifier === 'n',
							'tabler:globe color-dark-700': selectedreviewStatus?.identifier === 't'
						}"
					></i>

					{{ selectedreviewStatus?.title }}
				</div>
			</template>

			<template #option="slotProps">
				<div class="flex items-center text-sm">
					<div>{{ slotProps.option.title }}</div>
				</div>
			</template>
		</DropdownStatus>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { ReviewStatusList } from '@/types/cvTypes'

import Swal from 'sweetalert2/dist/sweetalert2.js';
import 'sweetalert2/src/sweetalert2.scss';
import { useAppStore } from '@/stores/app-store';

const { currentLng } = useAppStore();

const emit = defineEmits<{
  reviewStatusChange: [{ uid: string; newStatus: string }]
  statusChange: [string]

}>()

const props = withDefaults(defineProps<{
	jsonData: {
		identifier: string;
		reviewStatusCode: string;
		_links: { self: { href: string } };
	};
	reviewStatusOptions?: ReviewStatusList[];
	disabled?: boolean;
  /** ✅ E RE: vlera e ardhur nga parent (p.sh. kur Authoring → Review = 'd') */
  optionValue?: string;
}>(), {
  disabled: false,
  optionValue: undefined
})

const temporaryStatus = ref<string>(props.jsonData.reviewStatusCode)
const currentStatus   = ref<string>(props.jsonData.reviewStatusCode)

/** rifresko kur serveri ndryshon props.jsonData */
watch(
  () => props.jsonData.reviewStatusCode,
  (newVal) => {
    currentStatus.value   = newVal
    temporaryStatus.value = newVal
  }
)

/** ✅ rifresko menjëherë kur parent-i ndryshon vlerën (Authoring -> Review) */
watch(
  () => props.optionValue,
  (v) => {
    if (typeof v === 'string' && v !== currentStatus.value) {
      currentStatus.value   = v
      temporaryStatus.value = v
    }
  },
  { immediate: true }
)

const selectedreviewStatus = computed(() => {
  return props.reviewStatusOptions?.find(s => s.identifier === currentStatus.value) ?? { identifier: 'n', title: 'New' }
})

async function switchStatus(event: { value: string }) {
	const status = props.reviewStatusOptions?.find(item => item.identifier === event.value)
	const packageUID = props.jsonData.identifier

  const prev = currentStatus.value
  temporaryStatus.value = event.value
  if (!status) return

  const patchStatus = async () => {
    try {
      const selfHref = props.jsonData?._links?.self?.href
      if (!selfHref) {
        throw new Error('Missing _links.self.href for review status update')
      }

      const url = new URL(selfHref, window.location.origin)
      if (currentLng.value) {
        url.searchParams.set('lng', String(currentLng.value))
      }

      const response = await fetch(url.toString(), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reviewStatusCode: status.identifier })
      })
      if (!response.ok) throw new Error(`Failed with status ${response.status}`)

      currentStatus.value   = status.identifier
      temporaryStatus.value = status.identifier
      emit('reviewStatusChange', { uid: packageUID, newStatus: status.identifier })
	  emit('statusChange', status.identifier)


      await Swal.fire({
        title: 'Status Changed!',
        html: `Review status updated to <b>"<u>${status.title}</u>"</b>.`,
        icon: 'success',
        confirmButtonColor: '#15803d'
      })
    } catch (error) {
      console.error(error)
      // 🔁 rikthe UI në vlerën e mëparshme nëse PATCH dështoi
      currentStatus.value   = prev
      temporaryStatus.value = prev

      await Swal.fire({
        title: 'Update Failed',
        text: 'An error occurred while updating the status.',
        icon: 'error',
        confirmButtonColor: '#d33'
      })
    }
  }

  if (status.identifier === 'f') {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to change the review status of this item?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#4679cc',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes'
    })
    if (result.isConfirmed) {
      await patchStatus()
    } else {
      temporaryStatus.value = currentStatus.value
    }
  } else {
    await patchStatus()
  }
}
</script>
<style>
#reviewStatus .p-inputtext {
	padding-left: 0.5rem !important;
	padding-right: 0 !important;
	padding-top: 0.5rem !important;
	padding-bottom: 0.5rem !important;
}
</style>
